// From "GetScenarios"
// Note that the ONLY user params affecting this are
// UserChoicePercentageBound
// Intensity of "non-motorized transit"
import { createSelector } from 'reselect'

import { scenarioColors } from '../../data/config';
import {
  PASSENGER_DISTANCE_TRAVELED_COEFFICIENTS as PDT_COEFFICIENTS
} from '../../data/modelConstants';
import { IChartData } from '../../services/projections';

// Selectors
import {
  CURRENT_YEAR,
  getLastSeriesValue,
  getNumProjectionYears,
  getScenarios,
  getUserChoicePercentageBound,
  PROJECTION_START_YEAR,
  PROJECTION_YEAR_DELTA,
} from '../selectors';
import {
  getGdpPerCapitaSeries,
} from './gdp';

// Calculate value for the beginning and end
const calculatePassengerDistanceTravel = (upperBound: number, lowerBound: number, usePercentageBound: number): number => {
  return usePercentageBound * upperBound + (1 - usePercentageBound) * lowerBound;
}

// User-bounded version of "passengerDistanceTravel"
const calculateUserBoundPDT = (gdpPerCapita: number, percentageBound: number) => {
  const upperBound = PDT_COEFFICIENTS.slope.upperBound * gdpPerCapita + PDT_COEFFICIENTS.intercept.upperBound;
  const lowerBound = Math.max(0, PDT_COEFFICIENTS.slope.lowerBound * gdpPerCapita + PDT_COEFFICIENTS.intercept.lowerBound);

  return calculatePassengerDistanceTravel(upperBound, lowerBound, percentageBound);
}

// Calculate this for all of the scenarios...
// TODO: rewrite this in terms of using the internal computing function
export const getPassengerDistanceTraveledSeries = createSelector(
  [getGdpPerCapitaSeries, getUserChoicePercentageBound, getNumProjectionYears, getScenarios],
  (gdpPerCapitaSeries, userChoicePercentageBound, numProjectionYears, scenarios): IChartData[] => {

    // Process: calculate initial upper and lower bounds
    const baseGdpPerCapita = gdpPerCapitaSeries[PROJECTION_YEAR_DELTA].y;
    const endGdpPerCapita = getLastSeriesValue(gdpPerCapitaSeries);

    const startDistance = calculateUserBoundPDT(baseGdpPerCapita, userChoicePercentageBound);

    const charts: IChartData[] = [];
    const yearsInverse = (1 / (numProjectionYears - PROJECTION_YEAR_DELTA));
    scenarios.forEach(scenario => {
      // The encapsulation leaked here, but that's OK for now.
      const { modalSplitIntensity: { nonMotorizedTransit } } = scenario;
      const adjustedPercentageBound = Math.max(0, userChoicePercentageBound - (nonMotorizedTransit / 10));

      // end GDP per capita
      const endDistance = calculateUserBoundPDT(endGdpPerCapita, adjustedPercentageBound);

      // Once we have a growthRate, we can start filling in values
      const growthMultiplier = Math.pow((endDistance / startDistance), yearsInverse);

      // first value
      const distance = [{
        'x': CURRENT_YEAR,
        'y': startDistance
      }];

      let midDistance = startDistance;
      for (let i = 0; i < (numProjectionYears - PROJECTION_YEAR_DELTA); i++) {
        midDistance *= growthMultiplier
        distance.push({
          'x': CURRENT_YEAR + i + 1,
          'y': midDistance
        });
      }

      // then we can add a head

      const combined = distance;
      charts.push({
        color: scenarioColors[scenario.key],
        data: combined,
        id: scenario.key,
      });

      // DEBUGGING- confirming that GDP per capita plots correctly
      // charts.push({
      //   color: scenarioColors[scenario.key],
      //   data: gdpPerCapitaSeries,
      //   id: scenario.key,
      // })
    });

    return charts;
  }
)

// Used for actual math by other selectors
export const getPassengerDistanceTraveledSeriesMapping = createSelector(
  [getGdpPerCapitaSeries, getUserChoicePercentageBound, getNumProjectionYears, getScenarios],
  (gdpPerCapitaSeries, userChoicePercentageBound, numProjectionYears, scenarios) => {

    // Process: calculate initial upper and lower bounds
    const baseGdpPerCapita = gdpPerCapitaSeries[PROJECTION_YEAR_DELTA].y;
    const endGdpPerCapita = getLastSeriesValue(gdpPerCapitaSeries);

    const startDistance = calculateUserBoundPDT(baseGdpPerCapita, userChoicePercentageBound);

    const mapping: any = {}
    const yearsInverse = (1 / (numProjectionYears - PROJECTION_YEAR_DELTA));
    scenarios.forEach(scenario => {
      // The encapsulation leaked here, but that's OK for now.
      const { modalSplitIntensity: { nonMotorizedTransit } } = scenario;
      const adjustedPercentageBound = Math.max(0, userChoicePercentageBound - (nonMotorizedTransit / 10));
      const endDistance = calculateUserBoundPDT(endGdpPerCapita, adjustedPercentageBound);

      // Once we have a growthRate, we can start filling in values
      const growthMultiplier = Math.pow((endDistance / startDistance), yearsInverse);
      const distance = [startDistance];

      let midDistance = startDistance;
      for (let i = 0; i < (numProjectionYears - PROJECTION_YEAR_DELTA); i++) {
        midDistance *= growthMultiplier
        distance.push(midDistance);
      }

      const tail = []
      for (let i = 0; i < PROJECTION_YEAR_DELTA; i++) {
        tail.push(0)
      }
      const combined = tail.concat(distance);

      mapping[scenario.key] = combined;
    });

    return mapping;
  }
)

// Things that will map out final values...
export interface IChartMapping {
  [key: string]: number
}

// Extracts the most recent value... note that it's not scenario specific though.
// Maybe this should return all of them, and then the consumer finds the one that maps to its key...
export const getPassengerDistanceTraveled = createSelector(
  [getPassengerDistanceTraveledSeries],
  (pdtCharts): IChartMapping => {
    const mapping: IChartMapping = {};
    pdtCharts.forEach(chart => {
      mapping[chart.id] = getLastSeriesValue(chart.data);
    })
    return mapping;
  }
)



// WEBPACK FOOTER //
// ./src/store/selectors/passengerDistanceTraveled.ts